import { stopEmbed } from "../player";
import { type Grid, phaseOf, phaseShift, syncRate } from "./grid";

/**
 * One deck: an <audio> of our own with a pitch fader, a key lock, a jog
 * wheel's nudge and the track's grid beside it.
 *
 * Why an <audio> and not the Web Audio API: Bandcamp's CDN serves the
 * previews without CORS headers, so a page can play them and cannot read a
 * sample -- a MediaElementSource over one would be silence. Everything the
 * decks draw was computed on the server; the element only has to play, and
 * playbackRate is the fader. It is mutable on purpose: the waveform reads it
 * sixty times a second, and React is told when something a button shows has
 * changed (onChange), not on every frame.
 */

export type DeckData = {
  grid: Grid | null;  // null when the server found no steady pulse
  fitErr: number;     // seconds; how far the detector's beats sat from the grid
  peaks: Uint8Array;
  peaksHz: number;
  dur: number;
};

/** The fader's range, as the Speed test's: a 1210's. */
export const PITCH_RANGE = 16;

/** The grid is not to be trusted above this: the detector's own beats sat
 *  this far from it on average. Set from real tracks, not from theory -- a
 *  steady electronic record fits to about 0.05 s, because the detector
 *  itself jitters. */
export const UNSTEADY_ABOVE = 0.08;

/**
 * Rate and key lock, together, every time: playbackRate is a property of the
 * loaded media and resets when the src changes, and Safari spells the pitch
 * switch its own way.
 */
export function applyRate(el: HTMLAudioElement, rate: number, keyLock: boolean): void {
  el.playbackRate = rate;
  el.preservesPitch = keyLock;
  (el as HTMLAudioElement & { webkitPreservesPitch?: boolean }).webkitPreservesPitch = keyLock;
}

export class Deck {
  readonly el: HTMLAudioElement;
  data: DeckData | null = null;
  pitch = 0;        // percent, -PITCH_RANGE..PITCH_RANGE
  keyLock = false;  // keep the pitch while the tempo moves
  barShift = 0;     // which beat is the downbeat, 0-3
  /** Told when something a control displays has changed. */
  onChange: (() => void) | null = null;

  private bendRatio = 1;
  private bendTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.el = new Audio();
    this.el.preload = "auto";
    const changed = () => this.onChange?.();
    this.el.onplay = changed;
    this.el.onpause = changed;
    this.el.onended = changed;
    // The rate belongs to the media, not the element -- see applyRate.
    this.el.onloadedmetadata = () => { this.apply(); changed(); };
  }

  load(url: string, data: DeckData): void {
    this.el.pause();
    this.el.src = url;
    this.data = data;
    this.bendRatio = 1;
    this.apply();
    this.onChange?.();
  }

  unload(): void {
    this.el.pause();
    this.el.removeAttribute("src");
    this.el.load();
    this.data = null;
    this.onChange?.();
  }

  get loaded(): boolean { return this.data !== null; }
  get playing(): boolean { return this.loaded && !this.el.paused; }
  get grid(): Grid | null { return this.data?.grid ?? null; }

  /** The fader's rate, without the jog wheel's passing nudge. */
  get rate(): number { return 1 + this.pitch / 100; }

  private apply(): void {
    applyRate(this.el, this.rate * this.bendRatio, this.keyLock);
  }

  setPitch(pct: number): void {
    this.pitch = Math.max(-PITCH_RANGE, Math.min(PITCH_RANGE, pct));
    this.apply();
    this.onChange?.();
  }

  setKeyLock(on: boolean): void {
    this.keyLock = on;
    this.apply();
    this.onChange?.();
  }

  setBarShift(n: number): void {
    this.barShift = ((n % 4) + 4) % 4;
    this.onChange?.();
  }

  /**
   * A passing change of speed, as a hand on the platter: the rate is
   * multiplied by `ratio` for `ms`, then falls back to the fader's. Calling
   * again before it falls back extends it, so a wheel that keeps turning
   * keeps nudging.
   */
  bend(ratio: number, ms: number): void {
    this.bendRatio = ratio;
    this.apply();
    if (this.bendTimer) clearTimeout(this.bendTimer);
    this.bendTimer = setTimeout(() => {
      this.bendRatio = 1;
      this.bendTimer = null;
      this.apply();
    }, ms);
  }

  time(): number { return this.el.currentTime; }

  seek(t: number): void {
    const dur = Number.isFinite(this.el.duration) ? this.el.duration : this.data?.dur ?? t;
    this.el.currentTime = Math.max(0, Math.min(t, dur));
  }

  async play(): Promise<void> {
    if (!this.loaded) return;
    stopEmbed();
    await this.el.play();
  }

  pause(): void { this.el.pause(); }

  async toggle(): Promise<void> {
    if (this.playing) this.pause(); else await this.play();
  }

  /** Where in the beat the deck is now, or null without a grid. */
  phase(): number | null {
    return this.grid ? phaseOf(this.time(), this.grid) : null;
  }

  /** The tempo as heard: the grid's at the fader's rate. */
  effectiveBpm(): number | null {
    return this.grid ? this.grid.bpm * this.rate : null;
  }
}

/**
 * Sync: set the slave's fader so its tempo equals the master's as heard, then
 * move it so the nearest beats coincide. Seeking an <audio> lands within a
 * few tens of milliseconds; the jog wheel trims the rest by ear, as on real
 * decks. Returns what the rate did, or null when either deck has no grid.
 */
export function syncTo(slave: Deck, master: Deck): { clamped: boolean; neededPct: number } | null {
  const mg = master.grid, sg = slave.grid;
  if (!mg || !sg) return null;
  const r = syncRate({ bpm: mg.bpm, rate: master.rate }, { bpm: sg.bpm }, PITCH_RANGE);
  slave.setPitch((r.rate - 1) * 100);
  const shift = phaseShift(phaseOf(master.time(), mg), phaseOf(slave.time(), sg), 60 / sg.bpm);
  slave.seek(slave.time() + shift);
  return { clamped: r.clamped, neededPct: r.neededPct };
}
